import { useState } from "react";
import {
  Table,
  Image,
  Typography,
  Row,
  Button,
  Tooltip,
  Modal,
  message,
  Tag,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  GET_CATEGORIES,
  DELETE_CATEGORY,
  GET_CATEGORIES_SORTED,
} from "./requests";
import { Container } from "./elements";
import TableTitle from "./table-title";
import CreateCategory from "./components/createModal";
import EditCategory from "./components/editModal";
import { categoryTypes } from "../../types";
import PriorityCategory from "./components/sortModal";

const { Text } = Typography;

const Categories = () => {
  const [nameFilter, setNameFilter] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showPriorityModal, setShowPriorityModal] = useState<boolean>(false);
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 10 });
  const [categoryInfo, setCategoryInfo] =
    useState<typeof categoryTypes.category>();

  const [deleteCategoryMutation] = useMutation(DELETE_CATEGORY);

  const { data, loading, refetch } = useQuery(GET_CATEGORIES, {
    variables: {
      params: pageConfig,
      name: nameFilter,
    },
  });

  const [loadSortedCategories, { data: sortCategories }] = useLazyQuery(
    GET_CATEGORIES_SORTED,
    {
      variables: {
        params: { page: 1,
          pageSize: 100 },
      },
    },
  );

  const categories = data?.categories?.results ?? [];

  const showDeleteConfirm = (category: typeof categoryTypes.category) => {
    Modal.confirm({
      title: "¿Estás seguro de eliminar esta categoría?",
      content: `La categoría ${category.name} será eliminada.`,
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => handleDeleteCategory(category.id),
    });
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      await deleteCategoryMutation({
        variables: { id: categoryId },
      });
      message.success("Categoría eliminada correctamente");
      await refetch();
    } catch (error) {
      console.log(error);
      message.error("Error al eliminar la categoría");
    }
  };

  const columns = [
    {
      title: "",
      dataIndex: "img",
      key: "img",
      width: "8%",
      render: (img: string) => {
        if (img && img !== "") {
          return <Image width={50} src={img} />;
        } else {
          return null;
        }
      },
    },
    {
      title: "Categoria",

      key: "name",
      render: (row: any) => (
        <div>
          <Text strong>{row?.name}</Text>
          <br />
          <Text style={{ color: "grey" }}>{row?.description}</Text>
        </div>
      ),
    },

    {
      title: "Productos",
      dataIndex: "products",
      key: "products",
      render: (products: (typeof categoryTypes.category)["products"]) => {
        return (
          <Tag color="green" strong>
            {products ? products.length : 0}
          </Tag>
        );
      },
    },
    {
      title: "Acción",
      key: "action",

      render: (row: typeof categoryTypes.category) => (
        <Row>
          <Tooltip placement="top" title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined style={{ color: "white" }} />}
              style={{ margin: 5 }}
              onClick={() => {
                setShowEditModal(true);
                setCategoryInfo(row);
              }}
            >
              Editar
            </Button>
          </Tooltip>
          <Tooltip placement="top" title="Eliminar">
            <Button
              icon={<DeleteOutlined style={{ color: "white" }} />}
              style={{
                margin: 5,
                backgroundColor: "red",
                borderColor: "red",
                color: "white",
              }}
              onClick={() => showDeleteConfirm(row)}
            >
              Borrar
            </Button>
          </Tooltip>
        </Row>
      ),
    },
  ];

  return (
    <Container
      style={{
        overflow: "scroll",
      }}
    >
      <Table
        title={() => (
          <TableTitle
            setNameFilter={setNameFilter}
            setShowCreateModal={setShowCreateModal}
            setShowPriorityModal={(value) => {
              setShowPriorityModal(value);
              if (value) {
                loadSortedCategories();
              }
            }}
          />
        )}
        dataSource={categories}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          current: Number(pageConfig.page),
          defaultCurrent: pageConfig.page,
          pageSize: Number(pageConfig.pageSize),
          defaultPageSize: pageConfig.pageSize,
          total: data?.categories?.info?.count,
          showTotal: (total) => <Text disabled>{`${total} resultados`}</Text>,
          onChange: (page, pageSize) => {
            setPageConfig({
              page,
              pageSize,
            });
          },
          showSizeChanger: true,
        }}
      />
      <CreateCategory
        showModal={showCreateModal}
        setShowModal={setShowCreateModal}
        refetch={refetch}
        sortRefetch={loadSortedCategories}
      />
      <EditCategory
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        refetch={refetch}
        category={categoryInfo}
        setCategory={setCategoryInfo}
      />
      <PriorityCategory
        showModal={showPriorityModal}
        setShowModal={setShowPriorityModal}
        refetch={refetch}
        data={sortCategories}
        sortRefetch={loadSortedCategories}
      />
    </Container>
  );
};

export default Categories;
